// ============================================================================
// SERVICE CATEGORY TYPE
// ============================================================================
export interface ServiceCategory {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// SERVICE OPTION TYPE (all pricing here)
// ============================================================================
export interface ServiceOption {
  id: string;
  service_id: string;
  name: string;
  description: string | null;
  price: number | null; // NULL for quote-based options
  min_price: number | null; // For quote-based options only
  max_price: number | null; // For quote-based options only
  is_default: boolean; // True for primary/default option per service
  display_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// SERVICE TYPE (no pricing - pricing lives in options)
// ============================================================================
export interface Service {
  id: string;
  category_id: string;
  name: string;
  description: string | null;
  is_quote_based: boolean;
  display_order: number;
  active: boolean;
  created_at: string;
  updated_at: string;
  options?: ServiceOption[]; // Nested options
}

// ============================================================================
// SERVICE WITH CATEGORY (for display)
// ============================================================================
export interface ServiceWithCategory extends Service {
  category?: ServiceCategory;
}

// ============================================================================
// ORDER SERVICE TYPE (locked-in pricing at order time)
// ============================================================================
export interface OrderService {
  id: string;
  order_id: string;
  service_id: string;
  quantity: number;
  base_price: number; // Locked-in option price at order time
  selected_options: OrderServiceOption[];
  options_total: number; // Sum of all selected add-on prices
  service_total: number; // base_price * quantity + options_total
  created_at: string;
  updated_at: string;
  // Optional: service details for display
  service?: Service;
}

export interface OrderServiceOption {
  id: string;
  name: string;
  price: number;
}

// ============================================================================
// ORDER SERVICE INPUT (for creating/updating)
// ============================================================================
export interface OrderServiceInput {
  service_id: string;
  service_option_id: string; // Selected primary option (must have price or be quote-based)
  quantity?: number;
  selected_addon_option_ids?: string[]; // Optional add-on option IDs
}

// ============================================================================
// PRICING CALCULATION RESULT (frontend only)
// ============================================================================
export interface ServicePricingInfo {
  option_id: string;
  option_name: string;
  quantity: number;
  unit_price: number | null;
  min_price: number | null;
  max_price: number | null;
  is_quote_based: boolean;
  addons: Array<{
    id: string;
    name: string;
    price: number;
  }>;
  line_total: number | null; // null for quote-based
}

export interface PricingCalculation {
  services: ServicePricingInfo[];
  subtotal: number | null; // null if any service is quote-based
  has_quote_based: boolean;
  total: number | null;
}

// ============================================================================
// SERVICES RESPONSE (for API endpoints)
// ============================================================================
export interface ServicesResponse {
  success: boolean;
  data?: Service[] | ServiceWithCategory[] | Service;
  error?: string;
}
// ============================================================================
// STANDARD EDITING SERVICE CONFIG (new architecture)
// ============================================================================
export interface StandardEditingService {
  id: string;
  name: string;
  description: string | null;
  category: 'editing' | 'staging' | 'enhancement' | 'conversion';
  allows_reference: boolean;
  requires_reference: boolean;
  requires_per_image_instructions: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// IMAGE WITH PAIRING SUPPORT (new architecture)
// ============================================================================
export interface OrderImage {
  id: string;
  order_id: string;
  original_image_url: string;
  r2_key: string | null;
  storage_path: string | null;
  parent_image_id: string | null; // Links reference/edited to raw original
  image_instructions: string | null; // Per-image text instructions
  role: 'raw' | 'reference' | 'edited';
  file_name: string | null;
  display_order: number | null;
  created_at: string;
}

// ============================================================================
// IMAGE TRIPLET (for order details/editor workspace)
// ============================================================================
export interface ImageTriplet {
  id: string; // original image id
  display_order: number;
  original: {
    id: string;
    url: string;
    r2_key: string;
    file_name: string;
  };
  references?: Array<{
    id: string;
    url: string;
    r2_key: string;
    file_name: string;
  }>;
  edited?: {
    id: string;
    url: string;
    r2_key: string;
    file_name: string;
    created_at: string;
  };
  image_instructions?: string;
  status: 'waiting' | 'completed';
}

// ============================================================================
// IMAGE GROUPING UTILITY
// ============================================================================
export function groupImagesByParent(images: OrderImage[]): ImageTriplet[] {
  console.log('🔍 groupImagesByParent - Input images:', images);
  
  // Create map of parent_image_id → children
  const childrenMap = new Map<string, OrderImage[]>();
  const originals: OrderImage[] = [];

  images.forEach(img => {
    // An image is an "original" if it has role='raw' AND either:
    // 1. parent_image_id is null, OR
    // 2. parent_image_id equals its own id (self-referencing)
    if (img.role === 'raw' && (img.parent_image_id === null || img.parent_image_id === img.id)) {
      originals.push(img);
    } else if (img.parent_image_id && img.parent_image_id !== img.id) {
      // Only add to children if it has a parent AND it's not self-referencing
      if (!childrenMap.has(img.parent_image_id)) {
        childrenMap.set(img.parent_image_id, []);
      }
      childrenMap.get(img.parent_image_id)!.push(img);
    }
  });

  console.log('🔍 groupImagesByParent - Originals found:', originals);
  console.log('🔍 groupImagesByParent - Children map:', childrenMap);

  // Build triplets
  const triplets = originals.map(original => {
    const children = childrenMap.get(original.id) || [];
    const references = children
      .filter(c => c.role === 'reference')
      .map(ref => ({
        id: ref.id,
        url: ref.original_image_url,
        r2_key: ref.r2_key || '',
        file_name: ref.file_name || 'reference.jpg'
      }));
    const edited = children.find(c => c.role === 'edited');

    return {
      id: original.id,
      display_order: original.display_order || 0,
      original: {
        id: original.id,
        url: original.original_image_url,
        r2_key: original.r2_key || '',
        file_name: original.file_name || 'image.jpg'
      },
      references: references.length > 0 ? references : undefined,
      edited: edited ? {
        id: edited.id,
        url: edited.original_image_url,
        r2_key: edited.r2_key || '',
        file_name: edited.file_name || 'edited.jpg',
        created_at: edited.created_at
      } : undefined,
      image_instructions: original.image_instructions || undefined,
      status: (edited ? 'completed' : 'waiting') as 'completed' | 'waiting'
    };
  });

  // Sort by display_order
  return triplets.sort((a, b) => a.display_order - b.display_order);
}

// ============================================================================
// STANDARD EDITING ORDER INPUT (new architecture)
// ============================================================================
export interface StandardEditingOrderInput {
  service_id: string;
  images: Array<{
    original_file: File;
    reference_file?: File;
    instructions?: string;
  }>;
  global_instructions?: string;
}

// ============================================================================
// ORDER TYPE (HDR vs Standard)
// ============================================================================
export type OrderType = 'standard' | 'hdr';